import type { DropdownMenuItemProps } from '@radix-ui/react-dropdown-menu';
import { Item } from '@radix-ui/react-dropdown-menu';
import classNames from 'classnames';
import type { ReactNode } from 'react';
import { Spacer } from '../Spacer';
import { TextBase } from '../Typography';

export interface DropdownItemProps {
  children?: ReactNode;
  disabled?: boolean;
  onClick?: (e: Event) => void;
  label?: string | ReactNode;
  iconLeft?: ReactNode;
  iconRight?: ReactNode;
  asChild?: boolean;
  'aria-label'?: string;
}

const MenuItem = ({
  disabled,
  onSelect,
  children,
  asChild,
  'aria-label': ariaLabel,
}: DropdownMenuItemProps) => (
  <Item
    className={classNames(
      'group data-[highlighted]:bg-ds-natural-50 outline-none data-[disabled]:pointer-events-none',
      onSelect ? 'cursor-pointer' : 'cursor-default'
    )}
    onSelect={onSelect}
    onClick={(event) => event.stopPropagation()}
    disabled={disabled}
    asChild={asChild}
    aria-label={ariaLabel}
  >
    {children}
  </Item>
);

const DecoratedMenuItem = ({
  disabled,
  onSelect,
  children,
  'aria-label': ariaLabel,
}: DropdownMenuItemProps) => (
  <Item
    className="group data-[highlighted]:bg-ds-natural-50 outline-none data-[disabled]:pointer-events-none"
    onSelect={onSelect}
    onClick={(event) => event.stopPropagation()}
    disabled={disabled}
    aria-label={ariaLabel}
  >
    {children}
  </Item>
);

export const DecoratedMenuItemContent = ({
  label,
  disabled,
  iconLeft,
  iconRight,
}: Pick<
  DropdownItemProps,
  'label' | 'iconLeft' | 'iconRight' | 'disabled'
>) => (
  <div
    className={classNames(
      'flex items-center justify-between rounded p-2.5',
      disabled ? 'cursor-default opacity-50' : 'cursor-pointer'
    )}
  >
    <div className="flex items-center justify-start min-w-0">
      {iconLeft && <div className="mr-2 text-ds-natural-600">{iconLeft}</div>}
      <div className="truncate [&>span]:group-data-[highlighted]:text-ds-natural-600 [&>span]:group-data-[highlighted]:group-active:text-ds-natural-600">
        {typeof label === 'string' ? (
          <TextBase
            label={label}
            color="text-ds-natural-600"
            ellipsis
          />
        ) : (
          label
        )}
      </div>
      <Spacer amount={0.75} />
    </div>
    {iconRight && <div className="text-ds-natural-600">{iconRight}</div>}
  </div>
);

export const DropdownItem = ({
  children,
  disabled,
  onClick,
  label,
  iconLeft,
  iconRight,
  asChild,
  'aria-label': ariaLabel,
}: DropdownItemProps) =>
  children ? (
    <MenuItem
      asChild={asChild}
      disabled={disabled}
      onSelect={onClick}
      aria-label={ariaLabel}
    >
      {children}
    </MenuItem>
  ) : (
    <DecoratedMenuItem
      disabled={disabled}
      onSelect={onClick}
      aria-label={ariaLabel}
    >
      <DecoratedMenuItemContent {...{ label, disabled, iconLeft, iconRight }} />
    </DecoratedMenuItem>
  );
